import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { UserCog, Loader2 } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import { useWorkspace } from "../../lib/WorkspaceContext";
import { formatApiErrorDetail } from "../../lib/api";
import { formatMemberSince } from "../../pages/profileSections";
import { Button } from "../ui/button";
import RoleBadge from "../RoleBadge";

export default function ProfileIdentitySection() {
  const { t, i18n } = useTranslation("common");
  const { user, updateProfile } = useAuth();
  const { current } = useWorkspace();
  const [editing, setEditing] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const T = (k, o) => t(`settings.profile.${k}`, o);

  const startEdit = () => {
    setNameValue(user?.name || "");
    setError("");
    setSuccess(false);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError("");
  };

  const trimmedName = nameValue.trim();
  const saveDisabled = busy || trimmedName === "" || trimmedName === (user?.name || "");

  const saveName = async () => {
    if (saveDisabled) return;
    setBusy(true);
    setError("");
    try {
      await updateProfile({ name: trimmedName });
      setSuccess(true);
      setEditing(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || T("error_fallback"));
    } finally {
      setBusy(false);
    }
  };

  const memberSince = formatMemberSince(user?.created_at, i18n.language);

  return (
    <section className="border border-zinc-200 rounded-2xl bg-white p-6 mb-6" data-testid="profile-identity-section">
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-100 text-zinc-700">
          <UserCog size={18} strokeWidth={1.75} />
        </span>
        <div>
          <h2 className="font-display text-lg font-semibold text-zinc-950">{T("identity.title")}</h2>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-md">{T("identity.subtitle")}</p>
        </div>
      </div>

      {success && (
        <div
          className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm px-3 py-2 mb-4"
          data-testid="profile-name-success"
        >
          {T("identity.saved")}
        </div>
      )}

      {error && (
        <div
          role="alert"
          aria-live="assertive"
          className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm px-3 py-2 mb-4"
          data-testid="profile-name-error"
        >
          {error}
        </div>
      )}

      <div className="divide-y divide-zinc-100">
        {/* Nom */}
        <div className="py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-zinc-500 mb-1">{T("identity.name_label")}</p>
            {!editing ? (
              <p
                className={`text-sm ${user?.name ? "text-zinc-800" : "text-zinc-400"}`}
                data-testid="profile-name-value"
              >
                {user?.name || T("identity.name_empty")}
              </p>
            ) : (
              <input
                type="text"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                data-testid="profile-name-input"
                className="w-full max-w-xs h-10 px-3 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300"
                autoFocus
              />
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!editing ? (
              <button
                type="button"
                onClick={startEdit}
                data-testid="profile-name-edit"
                className="text-sm text-zinc-950 underline"
              >
                {T("identity.edit_cta")}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={cancelEdit}
                  data-testid="profile-name-cancel"
                  className="text-sm text-zinc-500 hover:text-zinc-700"
                >
                  {T("identity.cancel")}
                </button>
                <Button
                  type="button"
                  onClick={saveName}
                  disabled={saveDisabled}
                  data-testid="profile-name-save"
                  className="h-10 bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : T("identity.save")}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* E-mail */}
        <div className="py-3">
          <p className="text-xs text-zinc-500 mb-1">{T("identity.email_label")}</p>
          <div className="flex items-center gap-2">
            <p className="text-sm text-zinc-800" data-testid="profile-email-value">{user?.email}</p>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
                user?.email_verification_status === "ok"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-700"
              }`}
              data-testid="profile-email-status"
            >
              {user?.email_verification_status === "ok" ? T("identity.email_verified") : T("identity.email_unverified")}
            </span>
          </div>
        </div>

        {/* Role */}
        {current?.role && (
          <div className="py-3">
            <p className="text-xs text-zinc-500 mb-1">{T("identity.role_label")}</p>
            <div className="flex items-center gap-2" data-testid="profile-role-value">
              <RoleBadge role={current.role} />
              {user?.role === "admin" && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-zinc-100 text-zinc-600"
                  data-testid="profile-role-platform-admin"
                >
                  {T("identity.platform_admin")}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Membre depuis */}
        {memberSince && (
          <div className="py-3">
            <p className="text-xs text-zinc-500 mb-1">{T("identity.member_since_label")}</p>
            <p className="text-sm text-zinc-800" data-testid="profile-member-since">{memberSince}</p>
          </div>
        )}

        {/* 2FA */}
        <div className="py-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-zinc-500 mb-1">{T("identity.two_factor_label")}</p>
            <p className="text-sm text-zinc-800" data-testid="profile-2fa-status">
              {user?.totp_enabled ? T("identity.two_factor_on") : T("identity.two_factor_off")}
            </p>
          </div>
          <Link to="/settings/security" data-testid="profile-2fa-link" className="text-sm text-zinc-950 underline">
            {T("identity.two_factor_link")}
          </Link>
        </div>
      </div>
    </section>
  );
}
